import React, { useState, useRef, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  AllCommunityModule,
  ModuleRegistry,
  ICellRendererParams,
  IGetRowsParams,
  RowStyle,
} from 'ag-grid-community';
import AddUserModal from '../../components/modals/AddUserModal';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { canManageUserRole } from '../../config/permissions';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface UserData {
  _id?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  timezone: string;
  active: boolean;
  clientId?: string;
  clientName?: string;
  role?: string;
  inviteEmailStatus?: 'sent' | 'delivered' | 'bounced' | 'failed' | 'spam';
  inviteAccepted?: boolean;
  permissions?: {
    editUsers: boolean;
    editKpis: boolean;
    editPlayers: boolean;
    editOpponents: boolean;
    editSeasons: boolean;
    editGames: boolean;
  };
  statsTracking?: {
    possession: boolean;
    shots: boolean;
    turnovers: boolean;
    clears: boolean;
    faceOffs: boolean;
    penalties: boolean;
  };
  [key: string]: any;
}

interface ClientData {
  _id: string;
  name: string;
  active: boolean;
}

const Users: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const [userToReset, setUserToReset] = useState<UserData | null>(null);
  const [editUser, setEditUser] = useState<UserData | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<string>('employees');
  const [clients, setClients] = useState<ClientData[]>([]);
  const gridApi = useRef<any>(null);

  const navigate = useNavigate();

  // Get user roles to determine permissions
  const isAdmin = sessionManager.isAdmin();
  const isEmployee = sessionManager.isEmployee();
  const canManageEmployees = sessionManager.canManageEmployees();
  const canAccessMultipleClients = isAdmin || isEmployee; // Admin or employees can see all clients filter

  // Fetch all clients on component mount
  useEffect(() => {
    fetchClients();
    // Set initial client filter for non-admin users
    if (!canAccessMultipleClients) {
      const currentUserClientId = sessionManager.getClientId();
      if (currentUserClientId) {
        setSelectedClientId(currentUserClientId);
      }
    }
  }, [canAccessMultipleClients]);

  const fetchClients = async () => {
    try {
      const response = await api.get(API_URLS.GET_ALL_ACTIVE_CLIENTS);
      if (response.data.success) {
        setClients(response.data.data || []);
      }
    } catch (error) {
      showErrorMsg('Failed to fetch clients.');
    }
  };

  // Define the datasource outside of onGridReady
  const datasource = useMemo(
    () => ({
      getRows: async (getRowsParams: IGetRowsParams) => {
        const page = Math.floor(getRowsParams.startRow / pageSize);
        const limit = getRowsParams.endRow - getRowsParams.startRow;

        if (getRowsParams.startRow === 0) {
          setIsLoading(true);
        }

        try {
          const params: any = {
            page: page + 1,
            limit: limit,
          };

          // Add clientId filter if a specific client is selected
          if (selectedClientId && selectedClientId !== 'all' && selectedClientId !== 'employees') {
            params.clientId = selectedClientId;
          } else if (selectedClientId === 'employees') {
            // Special case for employees
            params.clientId = 'employees';
            params.includeEmployees = true;
          } else if (!canAccessMultipleClients) {
            // For non-admin users, always filter by their client even if 'all' is selected
            const currentUserClientId = sessionManager.getClientId();
            if (currentUserClientId) {
              params.clientId = currentUserClientId;
            }
          }

          const response = await api.get(API_URLS.GET_USERS, { params });
          if (response.data.success) {
            getRowsParams.successCallback(response.data.data, response.data.total);
            if (getRowsParams.startRow === 0) {
              setIsLoading(false);
            }
          } else {
            showErrorMsg(response.data.message || 'Failed to fetch users');
            getRowsParams.failCallback();
            setIsLoading(false);
          }
        } catch (error) {
          showErrorMsg('Failed to fetch users.');
          getRowsParams.failCallback();
          setIsLoading(false);
        }
      },
    }),
    [pageSize, selectedClientId, canAccessMultipleClients],
  );

  // On grid ready, just save the api ref
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setIsLoading(true);
  };

  // Handle pagination changes (including page size changes)
  const onPaginationChanged = (params?: any) => {
    if (!gridApi.current) return;
    const newPageSize = gridApi.current.paginationGetPageSize();
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Edit button logic
  const handleEditClick = (user: UserData) => {
    setEditUser(user);
    setIsModalOpen(true);
  };

  // Delete button logic
  const handleDeleteClick = (user: UserData) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  // Password reset button logic
  const handleResetPasswordClick = (user: UserData) => {
    setUserToReset(user);
    setIsResetPasswordDialogOpen(true);
  };

  // Confirm password reset
  const handleResetPasswordConfirm = async () => {
    if (userToReset && userToReset._id) {
      try {
        const response = await api.put(API_URLS.RESET_USER_PASSWORD, {
          _id: userToReset._id,
        });
        if (response.data.success) {
          showSuccessMsg(response.data.message);
        } else {
          showErrorMsg(response.data.message || 'Failed to reset password');
        }
      } catch (error) {
        showErrorMsg('Failed to reset password.');
      }
    }
    setIsResetPasswordDialogOpen(false);
    setUserToReset(null);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (userToDelete && userToDelete._id) {
      try {
        const response = await api.delete(API_URLS.DELETE_USER, {
          params: { _id: userToDelete._id },
        });
        if (response.data.success) {
          showSuccessMsg('User deleted successfully!');
          if (gridApi.current) {
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to delete user');
        }
      } catch (error) {
        showErrorMsg('Failed to delete user.');
      }
    }
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Only refresh grid after add or update
  const handleUserAddedOrUpdated = () => {
    setIsModalOpen(false);
    setEditUser(null);
    if (gridApi.current) {
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Only close modal, do not refresh grid
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditUser(null);
  };

  // Custom cell renderer for status column
  const StatusCellRenderer: React.FC<ICellRendererParams<UserData>> = ({ value }) => {
    const isActive = value === true || value === 'Active';
    return (
      <span
        className={`px-2 py-1 rounded-full ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  // Custom cell renderer for invite status column
  const InviteStatusCellRenderer: React.FC<ICellRendererParams<UserData>> = ({ data }) => {
    if (!data) return null;

    if (data.inviteAccepted) {
      return <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">Accepted</span>;
    }

    const statusConfig: Record<string, { label: string; className: string }> = {
      sent: { label: 'Sent', className: 'bg-gray-100 text-gray-700' },
      delivered: { label: 'Delivered', className: 'bg-blue-100 text-blue-800' },
      bounced: { label: 'Bounced', className: 'bg-red-100 text-red-800' },
      failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
      spam: { label: 'Spam', className: 'bg-orange-100 text-orange-800' },
    };

    const status = data.inviteEmailStatus;
    if (!status) return null;

    const config = statusConfig[status];
    if (!config) return null;

    return <span className={`px-2 py-1 rounded-full ${config.className}`}>{config.label}</span>;
  };

  // Custom cell renderer for actions column
  const ActionsCellRenderer: React.FC<ICellRendererParams<UserData>> = ({ data }) => {
    if (!data) return null;

    // Check if this is an employee (no clientId means employee)
    const isEmployeeUser = !data.clientId;

    // Get current user info to determine permissions
    const currentUser = sessionManager.getUser();
    const isCurrentUser = currentUser && currentUser.id === data._id;

    // Sales and Marketing users cannot manage employees
    const canManageThisUser = isEmployeeUser ? canManageEmployees : true;

    // Check role hierarchy - user can only manage users with same or lower role
    let canManageBasedOnRole = true;
    if (!isEmployee && data.roleValue) {
      // Get current user's role value
      const currentUserRole = sessionManager.getClientRole();
      if (currentUserRole && currentUserRole.value) {
        canManageBasedOnRole = canManageUserRole(currentUserRole.value, data.roleValue);
      }
    }

    // If user has full user management permissions via custom permissions,
    // they can manage users up to their own role level (role hierarchy still applies)
    // Hide buttons if this is the current user, don't have permission, or role is higher
    const shouldShowButtons = canManageThisUser && !isCurrentUser && canManageBasedOnRole;

    return (
      <div className="flex items-center justify-center space-x-2">
        {shouldShowButtons ? (
          <>
            <button
              onClick={() => handleEditClick(data)}
              className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-all"
              title="Edit User"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
                />
              </svg>
            </button>
            <button
              onClick={() => handleResetPasswordClick(data)}
              className="text-orange-600 hover:text-orange-800 transform hover:scale-110 transition-all"
              title="Reset Password"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteClick(data)}
              className="text-red-600 hover:text-red-800 transform hover:scale-110 transition-all"
              title="Delete User"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </>
        ) : (
          <span className="text-gray-400 px-2">{isCurrentUser ? 'You' : 'Restricted'}</span>
        )}
      </div>
    );
  };

  // Custom cell renderer for roles column
  const RolesCellRenderer: React.FC<ICellRendererParams<UserData>> = ({ data }) => {
    if (!data) return null;

    // Backend now sends resolved role labels in the 'roleLabel' field
    const roleDisplay = data.roleLabel || (data.clientId ? 'Client User' : 'Employee');

    return <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">{roleDisplay}</span>;
  };

  const columnDefs: ColDef<UserData>[] = [
    {
      field: 'email',
      headerName: 'Email',
      sortable: true,
      flex: 2,
      minWidth: 200,
    },
    {
      headerName: 'Name',
      field: 'name',
      valueGetter: (params) => `${params.data?.firstName || ''} ${params.data?.lastName || ''}`.trim(),
      sortable: true,
      flex: 2,
      minWidth: 180,
    },
    {
      headerName: 'Role',
      cellRenderer: RolesCellRenderer,
      sortable: true,
      flex: 1.5,
      minWidth: 120,
      valueGetter: (params) => {
        if (!params.data) return '';
        // Backend now sends resolved role labels for sorting
        return params.data.roleLabel || (params.data.clientId ? 'Client User' : 'Employee');
      },
    },
    {
      field: 'active',
      headerName: 'Status',
      sortable: true,
      flex: 1,
      cellRenderer: StatusCellRenderer,
      minWidth: 100,
    },
    {
      headerName: 'Invite Status',
      cellRenderer: InviteStatusCellRenderer,
      sortable: true,
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) => {
        if (!params.data) return '';
        if (params.data.inviteAccepted) return 'Accepted';
        return params.data.inviteEmailStatus || '';
      },
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      width: 130,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 130,
      },
    },
  ];

  // Function to apply alternating row styles
  const getRowStyle = (params: any): RowStyle => {
    if (params.node.rowIndex % 2 === 1) {
      return { backgroundColor: 'rgb(var(--sd-mint))' };
    }
    return {};
  };

  // Handle client selection change
  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    if (gridApi.current) {
      setIsLoading(true);
      gridApi.current.purgeInfiniteCache();
    }
  };

  return (
    <div className="w-full p-4">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center mb-4">
        <div className="flex justify-between items-center space-x-4">
          <h1 className="text-xl xss:text-2xl font-bold">Users</h1>
          {canAccessMultipleClients && (
            <select
              className="px-3 py-2 w-full text-nowrap md:w-auto border rounded"
              value={selectedClientId}
              onChange={(e) => handleClientChange(e.target.value)}
            >
              {(isEmployee || isAdmin) && <option value="employees">Employees</option>}
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* Invite Stat Takers button - only for client users, not employees */}
          {selectedClientId !== 'employees' && !isEmployee && (
            <button
              onClick={() => navigate('/setup/stat-takers')}
              className="bg-blue-600 w-full text-nowrap md:w-auto text-white font-semibold px-4 py-1 rounded hover:bg-blue-700 transition-colors"
              title="Open stat takers invitation page in new tab"
            >
              📊 Invite Stat Takers
            </button>
          )}
          {(selectedClientId !== 'employees' || canManageEmployees) && (
            <button
              onClick={() => {
                setEditUser(null);
                setIsModalOpen(true);
              }}
              className="bg-sd-green w-full md:w-auto text-black font-semibold px-4 py-1 rounded hover:bg-sd-green transition-colors shadow-2xl"
            >
              Add User
            </button>
          )}
        </div>
      </div>

      <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100]}
          cacheBlockSize={pageSize}
          datasource={datasource}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          loading={isLoading}
          blockLoadDebounceMillis={50}
          maxBlocksInCache={2}
          infiniteInitialRowCount={pageSize}
          overlayLoadingTemplate={
            '<div role="status"><svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg><span class="sr-only">Loading...</span></div>'
          }
          overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No users found</span>'}
          getRowStyle={getRowStyle}
        />
      </div>

      {isModalOpen && (
        <AddUserModal
          onClose={handleModalClose}
          onUserAdded={handleUserAddedOrUpdated}
          editUser={editUser}
          showClientSelection={canAccessMultipleClients}
          defaultClientId={!canAccessMultipleClients ? sessionManager.getClientId() || undefined : undefined}
        />
      )}
      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete User"
          message={`Are you sure you want to delete ${userToDelete?.firstName} ${userToDelete?.lastName}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
      {isResetPasswordDialogOpen && (
        <ConfirmDialog
          isOpen={isResetPasswordDialogOpen}
          onClose={() => setIsResetPasswordDialogOpen(false)}
          onConfirm={handleResetPasswordConfirm}
          title="Reset Password"
          message={`Are you sure you want to reset the password for ${userToReset?.firstName} ${userToReset?.lastName}? A new temporary password will be sent to their email address (${userToReset?.email}).`}
          confirmText="Reset Password"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};

export default Users;
